import { AlertModal, Button, Select, showToast } from "@bcc-ui/ui-components";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Assets,
    getGradingActionOptions,
    GRADING_ACTION_DETAIL_KEY,
    StoredGradingAction,
    toGradingAction,
    toStoredGradingAction,
} from "domain/asset-management/assets";
import { AUTH_ASSET_EDIT } from "domain/authority";
import { Asset, assetService } from "services/asset-management/AssetService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";

interface Props {
    asset: Asset;
    testId?: string;
}

function toFormValue(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function readGradingAction(asset: Asset): StoredGradingAction | undefined {
    return (
        toStoredGradingAction(asset[GRADING_ACTION_DETAIL_KEY]) ??
        toStoredGradingAction(
            (asset.assetDetails ?? []).find((detail) => detail.key === GRADING_ACTION_DETAIL_KEY)?.value
        )
    );
}

function adoptToEditFormat(asset: Asset, gradingAction?: StoredGradingAction): Assets {
    return {
        assetId: asset.assetId,
        serialNumber: toFormValue(asset.serialNumber),
        assetType: toFormValue(asset.assetType),
        assetTag: toFormValue(asset.assetTag),
        manufacturer: toFormValue(asset.manufacturer),
        model: toFormValue(asset.model),
        assetState: toFormValue(asset.assetState),
        assignedTo: toFormValue(asset.assignedTo),
        location: toFormValue(asset.location),
        department: toFormValue(asset.department),
        region: toFormValue(asset.region),
        assetDetails: gradingAction === undefined ? [] : [{ key: GRADING_ACTION_DETAIL_KEY, value: gradingAction }],
    };
}

export const GradingActionSelect = (props: Props) => {
    const { t } = useTranslation();
    const [savedAction, setSavedAction] = useState<StoredGradingAction | undefined>(readGradingAction(props.asset));
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [selectVersion, setSelectVersion] = useState(0);
    const abortControllerRef = useRef(new AbortController());

    useEffect(() => () => abortControllerRef.current.abort(), []);

    const canEditAsset = userSessionService.userHasAllAuthorities([AUTH_ASSET_EDIT]);

    const saveGradingAction = (value: string | number) => {
        if (value === savedAction) {
            return;
        }
        const action = toGradingAction(value);
        if (action === undefined) {
            logger.error(`Grading action select offered an unknown action "${value}".`);
            return;
        }
        const abortController = abortControllerRef.current;
        const storedState = adoptToEditFormat(props.asset, savedAction);
        const editedState: Assets = {
            ...storedState,
            assetDetails: [{ key: GRADING_ACTION_DETAIL_KEY, value: action }],
        };
        setSaving(true);
        assetService
            .editAsset(props.asset.uuid, storedState, editedState, abortController)
            .then(() => {
                if (!abortController.signal.aborted) {
                    setSavedAction(action);
                    showToast(t("AssetManagement.value.gradingTable.actionSaved"), "success");
                }
            })
            .catch((exception) => {
                if (!abortController.signal.aborted) {
                    logger.error(
                        `Failed to save grading action "${action}" for asset ${props.asset.uuid}.`,
                        exception.message
                    );
                    setSaveFailed(true);
                    setSelectVersion((version) => version + 1);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setSaving(false);
                }
            });
    };

    return (
        <>
            <Select
                key={`gradingAction-${selectVersion}`}
                name="gradingAction"
                placeholder={t("AssetManagement.value.gradingTable.selectAction")}
                hideLabelSection
                disabled={saving || !canEditAsset}
                selectedByDefault={savedAction}
                testIds={{ input: props.testId }}
                items={getGradingActionOptions(savedAction).map((option) => ({
                    value: option.value,
                    label: t(option.translationKey),
                }))}
                onSelection={saveGradingAction}
            />
            <AlertModal
                variant="Error"
                title={t("AssetManagement.value.gradingTable.actionSaveFailedTitle")}
                isOpen={saveFailed}
                hideModal={() => setSaveFailed(false)}
                buttons={
                    <Button variant="Primary" onClick={() => setSaveFailed(false)}>
                        {t("Common.ok")}
                    </Button>
                }
            >
                {t("AssetManagement.value.gradingTable.actionSaveFailed")}
            </AlertModal>
        </>
    );
};
